/**
 * A small figure card showing an example clinic: used in the landing page hero
 * and beside the log-in and sign-up forms.
 *
 * The numbers are the sample clinic from docs/test-data/sample-products.csv,
 * and they add up. They are illustrations of real screens, never claims about
 * a customer — which is why every place that shows one says "example".
 */
import { c, mono } from '../theme.js';

export function HeroCard({
  title,
  sub,
  figure,
  figureUnit,
  tag,
  tagNote,
  rows,
}: {
  title: string;
  sub: string;
  figure: string;
  figureUnit?: string;
  tag: { text: string; tone: 'danger' | 'green' };
  tagNote?: string;
  /** [label, value, isBad?] */
  rows: [string, string, boolean?][];
}) {
  return (
    <div
      className="gs-card"
      style={{
        boxSizing: 'border-box',
        background: c.card,
        color: c.ink,
        borderRadius: 18,
        padding: '16px 16px 14px',
        boxShadow: '0 30px 44px -24px rgba(0,0,0,0.6)',
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 500 }}>{title}</div>
      <div style={{ fontSize: 12.5, color: c.muted2, marginTop: 2 }}>{sub}</div>
      <div style={{ fontSize: 28, fontWeight: 300, letterSpacing: '-0.03em', margin: '16px 0 8px' }}>
        {figure}
        {figureUnit && (
          <span style={{ fontSize: 14, letterSpacing: 0, color: c.muted2 }}> {figureUnit}</span>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          fontSize: 12.5,
          color: c.muted2,
          paddingBottom: 12,
          borderBottom: '1px solid #D6D3C9',
        }}
      >
        <span
          style={{
            background: tag.tone === 'danger' ? c.danger : c.green,
            color: c.white,
            borderRadius: 980,
            padding: '2px 6px',
            fontSize: 12,
          }}
        >
          {tag.text}
        </span>
        {tagNote}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          rowGap: 6,
          fontSize: 12.5,
          paddingTop: 11,
        }}
      >
        {rows.map(([name, value, bad]) => (
          <Row key={name} name={name} value={value} bad={bad === true} quiet={value === ''} />
        ))}
      </div>
    </div>
  );
}

function Row({
  name,
  value,
  bad,
  quiet,
}: {
  name: string;
  value: string;
  bad: boolean;
  quiet: boolean;
}) {
  return (
    <>
      <span style={quiet ? { color: c.muted2, fontFamily: mono, fontSize: 11 } : undefined}>
        {name}
      </span>
      <span style={{ color: bad ? '#8C3A1A' : c.muted2 }}>{value}</span>
    </>
  );
}
